import { Link } from "react-router-dom";
import LegalShell, { Section } from "../components/LegalShell";

const PrivacyPolicyPage: React.FC = () => (
  <LegalShell title="Privacy Policy" updated="June 25, 2026">
    <p>
      This Privacy Policy explains how Kinoes ("Kinoes", "we", "us") collects, uses, and
      protects information when you use our website and SMS-based service that helps a
      family decide what to do on a given evening.
    </p>

    <Section heading="Information we collect">
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>Account information.</strong> When an organizer signs in (via our
          authentication provider), we receive their name and email address.
        </li>
        <li>
          <strong>Family and member details.</strong> The organizer provides member
          display names, roles (adult/teen/child), and mobile numbers so we can send
          invitations.
        </li>
        <li>
          <strong>Location.</strong> With your permission, your browser may share your
          approximate location to determine whether you are near the group tonight. If you
          decline, you can still participate. We also store an origin city you provide.
        </li>
        <li>
          <strong>Session activity.</strong> Your opt-in status, answers to the AI's
          questions, the preferences you express, your votes, and any post-event feedback.
        </li>
        <li>
          <strong>Technical data.</strong> Standard log and device information generated
          when you use the site.
        </li>
      </ul>
    </Section>

    <Section heading="How we use information">
      <ul className="list-disc pl-6 space-y-1">
        <li>To coordinate a plan: send invitations, gather opt-ins, run the vote, and share the decision.</li>
        <li>To curate suggestions through our AI assistant and improve future suggestions for your family.</li>
        <li>To operate, secure, and improve the service.</li>
      </ul>
    </Section>

    <Section heading="SMS messaging">
      <p>
        We use your mobile number only to send the Kinoes messages described in our{" "}
        <Link to="/sms-policy" className="text-primary-600">SMS Terms &amp; Opt-In Policy</Link>.
        We do <strong>not</strong> sell, rent, or share your mobile number or SMS opt-in
        information with third parties or affiliates for their marketing purposes. Reply
        STOP to any message to opt out.
      </p>
    </Section>

    <Section heading="Service providers we share data with">
      <p>
        We share information only with vendors that help us run the service, under
        agreements that limit their use of it:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Twilio</strong> — to deliver SMS messages.</li>
        <li><strong>Anthropic (Claude)</strong> — to power the AI that curates options. We send the answers and preferences relevant to a session; we do not send your mobile number.</li>
        <li><strong>Auth0</strong> — to authenticate organizers.</li>
        <li><strong>Event data sources</strong> (such as Google Places, Ticketmaster, and Google Programmable Search) — to find real options near you. We send search terms and an approximate area, not your identity.</li>
        <li><strong>Google Cloud Platform</strong> — to host the service and store data.</li>
      </ul>
      <p>We do not sell your personal information.</p>
    </Section>

    <Section heading="Children">
      <p>
        Kinoes is operated by adult organizers on behalf of their family. Members under 13
        do not create accounts; an adult organizer adds them and is responsible for
        obtaining any necessary consent for their participation. If you believe a child's
        information was provided without proper authority, contact us and we will remove it.
      </p>
    </Section>

    <Section heading="Cookies, analytics &amp; advertising">
      <p>
        With your consent, we use cookies and similar technologies to understand how Kinoes
        is used and to measure our advertising. These may include Google Analytics (GA4) and
        advertising pixels from Google, Meta (Facebook/Instagram), and TikTok. We ask for your
        consent before any of these load, and you can decline without losing any features. We
        do not include your private invite links or magic-link tokens in the data sent to
        these providers. You can change your choice at any time by clearing your browser's
        site data for Kinoes.
      </p>
    </Section>

    <Section heading="Data retention">
      <p>
        We keep information for as long as needed to provide the service and improve your
        family's future suggestions, then delete or anonymize it. You may request deletion
        of your family's data at any time.
      </p>
    </Section>

    <Section heading="Your choices and rights">
      <p>
        You can opt out of SMS by replying STOP, decline location sharing in your browser,
        and request access to or deletion of your information by contacting us. Depending
        on where you live, you may have additional rights under applicable law.
      </p>
    </Section>

    <Section heading="Changes to this policy">
      <p>
        We may update this policy from time to time. We will revise the "Last updated"
        date above when we do.
      </p>
    </Section>

    <Section heading="Contact us">
      <p>
        Questions about this policy? Email{" "}
        <a className="text-primary-600" href="mailto:privacy@kinoes.com">privacy@kinoes.com</a>.
      </p>
    </Section>
  </LegalShell>
);

export default PrivacyPolicyPage;
